import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material';

import { ROUTES } from 'navigation/constants';
import ProgressBar from 'components/atoms/ProgressBar';
import colors from 'theme/emotion/colors';
import { ONBOARDING_PROGRESS_STEPS, ONBOARDING_STEPS } from '../../constants';
import { getRegistrationProgress } from '../../selectors';
import type { OnboardingStep } from '../../types';

// Import actual components
import NameEntry from '../NameEntry';
import IndustrySelection from '../IndustrySelection';
import RoleEntry from '../RoleEntry';
import EmailEntry from '../EmailEntry';
import CreatingAccount from '../CreatingAccount';

const OnboardingContainer = styled('div')({
  minHeight: '100vh',
  maxHeight: '100vh',
  overflow: 'hidden',
  background: colors.neutral.grey,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '@supports (height: 100dvh)': {
    minHeight: '100dvh',
    maxHeight: '100dvh',
  },
});

const ProgressBarContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '1rem 0',
  backgroundColor: colors.neutral.grey,
  borderBottom: `1px solid ${colors.neutral.grey}`,
  position: 'sticky',
  top: 0,
  zIndex: 10,
});

const ContentContainer = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

interface OnboardingFlowProps {}

const getStepFromPath = (pathname: string): OnboardingStep | null => {
  if (pathname.endsWith('/name')) return ONBOARDING_STEPS.NAME;
  if (pathname.endsWith('/industry')) return ONBOARDING_STEPS.INDUSTRY;
  if (pathname.endsWith('/role')) return ONBOARDING_STEPS.ROLE;
  if (pathname.endsWith('/email')) return ONBOARDING_STEPS.EMAIL;
  if (pathname.endsWith('/creating-account')) return ONBOARDING_STEPS.CREATING_ACCOUNT;
  return null;
};

const getProgressForStep = (step: OnboardingStep | null): number => {
  if (!step) return 0;
  return ONBOARDING_PROGRESS_STEPS[step] || 0;
};

const OnboardingFlow: React.FC<OnboardingFlowProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const progressFromRedux = useSelector(getRegistrationProgress);
  
  const currentStep = getStepFromPath(location.pathname);
  const progress = progressFromRedux || getProgressForStep(currentStep);

  // Auto-redirect to first step if user lands on base onboarding route
  useEffect(() => {
    if (location.pathname === '/onboarding' || location.pathname === '/onboarding/') {
      navigate(ROUTES.ONBOARDING.NAME.path, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Handle browser back button to maintain onboarding flow
  useEffect(() => {
    const handlePopState = () => {
      // Let React Router handle the navigation
      // Just check if we're still in onboarding
      const currentPath = location.pathname;
      const step = getStepFromPath(currentPath);
      
      // Only intervene if user navigates completely out of onboarding
      if (!currentPath.startsWith('/onboarding') && !step) {
        // User has navigated away from onboarding entirely
        // No action needed - let them navigate freely
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);

  return (
    <OnboardingContainer>
      {currentStep && currentStep !== ONBOARDING_STEPS.CREATING_ACCOUNT && (
        <ProgressBarContainer>
          <ProgressBar 
            progress={progress} 
            width={255} 
            height={4}
            progressColor={colors.primary.light}
          />
        </ProgressBarContainer>
      )}
      
      <ContentContainer>
        <Routes>
          <Route 
            path="name" 
            element={<NameEntry />} 
          />
          <Route 
            path="industry" 
            element={<IndustrySelection />} 
          />
          <Route 
            path="role" 
            element={<RoleEntry />} 
          />
          <Route 
            path="email" 
            element={<EmailEntry />} 
          />
          <Route 
            path="creating-account" 
            element={<CreatingAccount />} 
          />
          <Route 
            path="*" 
            element={<Navigate to={ROUTES.ONBOARDING.NAME.path} replace />} 
          />
        </Routes>
      </ContentContainer>
    </OnboardingContainer>
  );
};

export default OnboardingFlow; 